import { Component, OnInit, Input, ElementRef, HostListener, OnDestroy, AfterViewInit, Inject } from '@angular/core';
import { LoggerService } from '@wdpr/ra-angular-logger';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { CommonModule, DOCUMENT } from '@angular/common';
import { take } from 'rxjs';

import { IPicture } from '@finder/shared/components/picture/picture.interface';
import { FeatureDetectionService } from '@finder/shared/services/feature-detection/feature-detection.service';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import getDefaultImageSVGDataUri from '@finder/shared/utils/string/getDefaultImageSVGDataUri';
import { IntersectionDirective } from '@finder/shared/directives/intersection/intersection.directive';
import { CREATE_PRELOAD, CreatePreloadProvider } from '@finder/shared/utils/dom/createPreload';
import { LazyLoaderService } from '@finder/shared/services/lazy-loader/lazy-loader-service';

const COMPONENT_NAME = 'FINDER-PICTURE --';

declare const simpleParallax: any;

/**
 * Creates a picture component that accepts a desktop and mobile image.
 * The component can inherit object-fit sizing if "fit" is present in the class.
 *
 * <finder-picture [picture]="Picture" class?="fit"></finder-picture>
 */
@Component({
    selector: 'finder-picture',
    templateUrl: './picture.component.html',
    styleUrls: ['./picture.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        IntersectionDirective
    ],
})
export class PictureComponent implements OnInit, OnDestroy, AfterViewInit {

    /**
     * @member {IPicture} picture The picture object that contains the desktop and mobile image
     */
    @Input() picture: IPicture;
    @Input() lazyLoadOffset = 400; // pixels
    @Input() lazyLoad = true; // lazy load is enabled by default
    @Input() loadingType;

    public skeletonAspectStyling: any = {};
    public objectFitSupport = true;
    public polyfillUrl: string;
    public defaultImage: SafeUrl;
    public rootMargin: string;
    public loaded = false;
    public loading = true;
    public imageSrc: string;
    public imageSrcSet: string[] = [];
    private isDesktop: boolean;
    private imageUrl: string;
    private defaultSVGDataUri: string;
    private warningMessage = 'A desktop or mobile picture is required to display an image';
    private parallaxImage;

    constructor(
        private logger: LoggerService,
        private hostElement: ElementRef,
        private featureDetection: FeatureDetectionService,
        private sanitizer: DomSanitizer,
        private viewportDetection: ViewportDetectionService,
        private lazyLoader: LazyLoaderService,
        @Inject(DOCUMENT) private document: Document,
        @Inject(CREATE_PRELOAD) private createPreload: CreatePreloadProvider,
    ) { }

    ngOnDestroy(): void {
        // Clean up the parallax image when leaving the page
        if (this.parallaxImage) {
            this.parallaxImage.destroy();
        }
    }

    @HostListener('transitionend', ['$event'])
    onTransitionEnd($event) {
        // After the image is loaded and the fade in
        // effect finishes remove the css transition property
        // to no be in conflic with other transition properties
        // applied to the image.
        this.loading = false;
    }

    public getElementRef(): Element {
        return this.hostElement.nativeElement;
    }

    ngOnInit() {
        this.isDesktop = this.viewportDetection.isDesktop();

        // When the image loading type is eager we need to make sure this value is set
        this.imageUrl = this.isDesktop ? this.picture?.desktop : this.picture?.mobile;

        if (this.lazyLoad) {
            this.rootMargin = `${this.lazyLoadOffset}px 0px`;
            this.defaultSVGDataUri = getDefaultImageSVGDataUri(this.imageUrl);
            this.defaultImage = this.sanitizer.bypassSecurityTrustUrl(this.defaultSVGDataUri);
            this.skeletonAspectStyling = this.buildSkeletonStyle(this.picture.metrics);
        } else {
            this.loading = false;
            this.loaded = true;
            this.init();
        }

        this.loadingType = this.loadingType ? this.loadingType : 'lazy';
        this.preloadImage();
    }

    ngAfterViewInit(): void {
        this.checkForParallax();
    }

    init() {
        /**
         * Check to see if object-fit is supported.
         * If fit class is applied to the component apply polyfill
         *
         * Based on this PR
         * https://github.disney.com/wdpro-development/wdpro-finder/pull/1358/files
         */
        if (!this.featureDetection.hasObjectFit() && this.getElementRef().classList.contains('fit')) {
            this.objectFitSupport = false;
            this.polyfillUrl = `url(${this.picture.desktop})`;
        }

        // Check for media if desktop and mobile are present
        if (this.picture.desktop && this.picture.mobile && !this.picture.media) {
            this.logger.warn(`${COMPONENT_NAME} A media query must be present if a desktop and mobile image is specified`);
        }

        // Make sure we have either a desktop or mobile picture
        if (!this.picture.desktop && !this.picture.mobile) {
            this.logger.warn(`${COMPONENT_NAME} ${this.warningMessage}`);
        }

        // Let's check for media metrics and set the image widths if they exist
        // This helps the browser pick the best image to load
        if (this.picture.metrics?.desktop && this.picture.desktop) {
            // Example: "https://cdn/image.png 1920w"
            this.picture.desktop += ` ${this.picture.metrics.desktop.width}w`;
        }
        if (this.picture.metrics?.mobile && this.picture.mobile) {
            // Example: "https://cdn/image.png 480w"
            this.picture.mobile += ` ${this.picture.metrics.mobile.width}w`;
        }

        // Add URLs to the array to create the srcset
        if (this.picture.desktop) {
            this.imageSrcSet.push(this.picture.desktop);
        }
        if (this.picture.mobile) {
            this.imageSrcSet.push(this.picture.mobile);
        }

        // Create the srcset string
        this.imageSrc = this.imageSrcSet.join(', ');
    }

    onLoad(event) {
        if (event.target.src === this.defaultSVGDataUri) {
            return;
        }
        this.loaded = true;
    }

    onIntersectionObserver(value) {
        if (!value.isIntersecting) {
            return;
        }
        const { desktop, mobile } = this.picture;
        // Make sure we have either a desktop or mobile picture
        if (!desktop && !mobile) {
            this.logger.warn(`${COMPONENT_NAME} ${this.warningMessage}`);
        }
        // preload the image
        const image = new Image();
        // if we are in desktop and we only have a mobile size image
        // make sure we load it
        image.src = this.imageUrl || desktop || mobile;

        image.onload = () => {
            this.init();
        };

        return image;
    }

    private buildSkeletonStyle(imageMetrics) {
        const stylesObject = { aspectRation: '16 / 9' };
        if (!imageMetrics) {
            return stylesObject;
        }

        if (this.isDesktop && imageMetrics?.desktop) {
            stylesObject.aspectRation = `${imageMetrics.desktop.width} / ${imageMetrics.desktop.height}`;
        }
        if (!this.isDesktop && imageMetrics?.mobile) {
            stylesObject.aspectRation = `${imageMetrics.mobile.width} / ${imageMetrics.mobile.height}`;
        }

        return stylesObject;
    }

    private checkForParallax() {
        if (!this.picture.parallax) {
            return;
        }

        this.lazyLoader.lazyLoadScriptResource(`simpleParallax.js`).pipe(
            take(1)
        ).subscribe(() => {
            // @TODO: / @SSR: Don't forget to fix this inclusion
            this.parallaxImage =
                new simpleParallax(this.getElementRef().querySelector('.picture-img'), { scale: Number(this.picture.parallaxScale) });
        });


    }

    private preloadImage() {
        if (this.loadingType !== 'eager') {
            return;
        }

        // Check for an image before loading it
        if (!this.imageUrl) {
            return;
        }

        /**
         * @TODO:
         * We need to add a mobile and a desktop image preload. Make sure to add media queries to them
         * https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/rel/preload#including_media
         *
         * This is going to require a bit more work. The "media" property on the `link` doesn't responsively load
         * the correct image.
         * https://web.dev/articles/preload-responsive-images
         *
         * Also I don't know that we should preload every image on the page. We should probably precalculate
         * the height they take up and allow them to lazyload so things load faster on slow mobile connections.
         */

        this.createPreload(this.document, this.imageUrl, 'image');
    }
}
